import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, ArrowRight, Home } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";
import { API } from "../App";

const PaymentResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error, cancelled
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get("session_id");
  const invoiceId = searchParams.get("invoice_id");
  const bookingId = searchParams.get("booking_id");
  const planKey = searchParams.get("plan");

  const pollPaymentStatus = useCallback(async (currentPollCount) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (currentPollCount >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(response.data);
      } else if (response.data.status === "expired") {
        setStatus("error");
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(() => pollPaymentStatus(currentPollCount + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(pollCount);
    } else if (invoiceId || bookingId) {
      // Cancelled payment
      setStatus("cancelled");
    }
  }, [sessionId, invoiceId, bookingId, pollPaymentStatus, pollCount]);

  const getReturnPath = () => {
    if (planKey) return "/dashboard/billing";
    if (invoiceId) return "/portal";
    if (bookingId) return "/portal";
    return "/";
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="bg-card border-border">
          <CardContent className="pt-12 pb-8 text-center">
            {status === "loading" && (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 mx-auto mb-6"
                >
                  <Loader2 className="w-16 h-16 text-primary" />
                </motion.div>
                <h1 className="text-2xl font-serif mb-2">Processing Payment</h1>
                <p className="text-muted-foreground">
                  Please wait while we confirm your payment...
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center"
                >
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </motion.div>
                <h1 className="text-2xl font-serif mb-2">Payment Successful!</h1>
                <p className="text-muted-foreground mb-6">
                  Thank you for your payment. Your transaction has been completed.
                </p>
                
                {paymentData && (
                  <div className="bg-muted/30 rounded-lg p-4 mb-6 text-left">
                    <div className="flex justify-between mb-2">
                      <span className="text-muted-foreground">Amount Paid</span>
                      <span className="font-medium">${paymentData.amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <span className="text-green-500 font-medium">Completed</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <Button onClick={() => navigate(getReturnPath())} className="btn-primary">
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    <Home className="w-4 h-4 mr-2" />
                    Go to Home
                  </Button>
                </div>
              </>
            )}

            {status === "error" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center"
                >
                  <XCircle className="w-12 h-12 text-red-500" />
                </motion.div>
                <h1 className="text-2xl font-serif mb-2">Payment Failed</h1>
                <p className="text-muted-foreground mb-6">
                  We couldn&apos;t process your payment. Please try again or contact support.
                </p>
                <div className="flex flex-col gap-3">
                  <Button onClick={() => window.history.back()} className="btn-primary">
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    <Home className="w-4 h-4 mr-2" />
                    Go to Home
                  </Button>
                </div>
              </>
            )}

            {status === "cancelled" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 mx-auto mb-6 bg-yellow-500/20 rounded-full flex items-center justify-center"
                >
                  <XCircle className="w-12 h-12 text-yellow-500" />
                </motion.div>
                <h1 className="text-2xl font-serif mb-2">Payment Cancelled</h1>
                <p className="text-muted-foreground mb-6">
                  Your payment was cancelled. No charges have been made.
                </p>
                <div className="flex flex-col gap-3">
                  <Button onClick={() => window.history.back()} className="btn-primary">
                    Return to Checkout
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    <Home className="w-4 h-4 mr-2" />
                    Go to Home
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentResultPage;
